/* Branded school header + cross-school leaderboard dialog used in every
 * School-System portal (Teacher / Head-teacher / Student-Parent).
 *
 * Feb 2026 (iter 107):
 *  - `SchoolBrandingHeader` renders the school's name, uploaded logo
 *    (if the HT has one) and a subtle location line.
 *  - `CheckPositionButton` opens a dialog with two ranked lists —
 *    "Top teachers" and "Top students" — computed cross-school by the
 *    /leaderboard endpoint. Tainapp uses this to present awards.
 */
import { useEffect, useState } from "react";
import { schoolSystemApi } from "@/lib/schoolSystemApi";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Trophy, Crown, GraduationCap, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export function SchoolBrandingHeader({ schoolId, portal, studentId }) {
  const [b, setB] = useState(null);
  useEffect(() => {
    if (!schoolId) return;
    schoolSystemApi.schoolBranding(schoolId)
      .then((r) => setB(r.school))
      .catch(() => setB(null));
  }, [schoolId]);

  // "Moved up X places this week" nudge (iter 108).
  // Fires at most once per role+scope per browser session so users
  // aren't nagged if they navigate between tabs. The backend also
  // dedupes weekly so this is safe to call every mount.
  useEffect(() => {
    if (portal !== "teacher" && portal !== "student") return;
    if (portal === "student" && !studentId) return;
    const scopeKey = `tain:rank-nudge:${portal}:${studentId || "self"}`;
    if (typeof window !== "undefined" && window.sessionStorage.getItem(scopeKey)) return;
    const params = portal === "student" ? { student_id: studentId } : {};
    schoolSystemApi.myLeaderboardPosition(params)
      .then((r) => {
        if (!r?.message) return;
        try { window.sessionStorage.setItem(scopeKey, "1"); } catch { /* ignore */ }
        const moved = (r.delta || 0);
        // Small delay so it lands after the portal has settled.
        setTimeout(() => {
          if (moved > 0) {
            toast.success(r.message, { icon: "🏆", duration: 6000 });
          } else if (moved < 0) {
            toast(r.message, { icon: "📉", duration: 5000 });
          } else {
            toast(r.message, { icon: "✨", duration: 4500 });
          }
        }, 900);
      })
      .catch(() => { /* silent — leaderboard is a nice-to-have */ });
  }, [portal, studentId]);

  const label = ({
    teacher: "Teachers Portal",
    headteacher: "Headteachers Portal",
    student: "Students Portal",
  })[portal] || "School Portal";
  const location = [b?.town, b?.district, b?.region, b?.country]
    .filter(Boolean).join(" · ");
  // If backend returned a relative /api/... path, prepend REACT_APP_BACKEND_URL
  // so <img> resolves the head-teacher-uploaded logo correctly.
  const rawLogo = b?.logo_url;
  const resolvedLogo = rawLogo && rawLogo.startsWith("/")
    ? `${process.env.REACT_APP_BACKEND_URL || ""}${rawLogo}`
    : rawLogo;
  return (
    <Card
      data-testid="school-branding-header"
      className="border-white/10 bg-gradient-to-br from-amber-500/[0.12] via-fuchsia-500/[0.08] to-sky-500/[0.10]"
    >
      <CardContent className="p-3 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="flex items-center gap-3 sm:gap-4 min-w-0">
          <div className="shrink-0 relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl border border-amber-300/30 bg-black/40 grid place-items-center overflow-hidden">
            {resolvedLogo ? (
              <img
                src={resolvedLogo}
                alt="School logo"
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.display = "none"; }}
              />
            ) : (
              <GraduationCap size={30} weight="fill" className="text-amber-300" />
            )}
            <Sparkle
              size={14}
              weight="fill"
              className="absolute -top-1 -right-1 text-amber-300 drop-shadow"
            />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] tracking-[0.24em] sm:tracking-[0.28em] uppercase text-amber-300 font-bold truncate">
              {label}
            </p>
            <h1
              data-testid="school-brand-name"
              className="font-heading text-xl sm:text-3xl font-black leading-tight truncate"
            >
              {b?.name || "Loading…"}
            </h1>
            {location && (
              <p className="text-[11px] sm:text-xs text-white/60 truncate">{location}</p>
            )}
          </div>
        </div>
        <div className="sm:ml-auto shrink-0 w-full sm:w-auto">
          <CheckPositionButton fullWidthOnMobile />
        </div>
      </CardContent>
    </Card>
  );
}

export function CheckPositionButton({ variant = "solid", fullWidthOnMobile = false }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await schoolSystemApi.leaderboard(20);
      setData(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Leaderboard unavailable");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (open && !data) load(); /* eslint-disable-next-line */ }, [open]);

  const cls = variant === "solid"
    ? "bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500 text-black hover:opacity-95 font-bold shadow-lg shadow-amber-400/20"
    : "border border-amber-300/50 bg-amber-500/10 hover:bg-amber-500/20 text-amber-200 font-bold";
  const widthCls = fullWidthOnMobile ? "w-full sm:w-auto" : "";

  return (
    <>
      <Button
        data-testid="check-position-btn"
        onClick={() => setOpen(true)}
        className={`gap-2 ${cls} ${widthCls}`}
      >
        <Trophy size={16} weight="fill" />
        Check your position
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="leaderboard-dialog"
          className="bg-[#0e0e0e] border-white/10 max-w-2xl w-[95vw] sm:w-auto max-h-[90vh] overflow-y-auto"
        >
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Crown size={20} weight="fill" className="text-amber-300" />
              Tainapp Leaders
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-white/60">
            Auto-calculated across every school on Tainapp. Teachers are
            ranked by how much they've contributed through the school
            system; students by a composite of test scores, attendance
            and homework scores. Top performers will receive Tainapp
            awards.
          </p>
          {loading ? (
            <p className="text-white/60 text-sm py-8 text-center">Loading…</p>
          ) : (
            <Tabs defaultValue="teachers" className="mt-2">
              <TabsList className="bg-white/5">
                <TabsTrigger value="teachers" data-testid="lb-tab-teachers">
                  Top teachers
                </TabsTrigger>
                <TabsTrigger value="students" data-testid="lb-tab-students">
                  Top students
                </TabsTrigger>
              </TabsList>
              <TabsContent value="teachers" className="mt-3">
                <RankTable
                  rows={(data?.teachers) || []}
                  cols={["rank", "name", "school", "grade", "score"]}
                  emptyText="No teacher activity recorded yet."
                  testIdPrefix="lb-teacher"
                />
              </TabsContent>
              <TabsContent value="students" className="mt-3">
                <RankTable
                  rows={(data?.students) || []}
                  cols={["rank", "name", "school", "grade", "test_avg", "attendance_pct", "homework_avg", "score"]}
                  emptyText="No student records recorded yet."
                  testIdPrefix="lb-student"
                />
              </TabsContent>
            </Tabs>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function RankTable({ rows, cols, emptyText, testIdPrefix }) {
  if (!rows?.length) {
    return <p className="text-white/50 text-sm py-6 text-center">{emptyText}</p>;
  }
  const label = { rank: "#", name: "Name", school: "School",
                   grade: "Class", score: "Score",
                   test_avg: "Tests", attendance_pct: "Attendance",
                   homework_avg: "Homework" };
  const podium = (r) => r === 1 ? "🥇" : r === 2 ? "🥈" : r === 3 ? "🥉" : String(r);
  const fmt = (v) => (v === null || v === undefined) ? "—" : `${v}%`;
  return (
    <div className="max-h-[420px] overflow-auto rounded-md border border-white/10">
      <table className="w-full text-xs sm:text-sm min-w-[560px]">
        <thead className="bg-white/5 sticky top-0">
          <tr>
            {cols.map((c) => (
              <th key={c} className="text-left px-2 sm:px-3 py-2 text-[10px] uppercase tracking-widest text-white/50 whitespace-nowrap">
                {label[c] || c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} data-testid={`${testIdPrefix}-row-${r.rank}`}
                 className="odd:bg-white/[0.02] hover:bg-white/[0.05]">
              {cols.map((c) => (
                <td key={c} className="px-2 sm:px-3 py-2 align-top whitespace-nowrap">
                  {c === "rank" ? (
                    <span className="font-bold">{podium(r.rank)}</span>
                  ) : c === "score" ? (
                    <span className="font-mono text-amber-300 font-bold">{r[c]}</span>
                  ) : (c === "test_avg" || c === "attendance_pct" || c === "homework_avg") ? (
                    <span className="font-mono text-white/70">{fmt(r[c])}</span>
                  ) : (
                    <span className={c === "name" ? "font-medium" : "text-white/70"}>
                      {r[c] || "—"}
                    </span>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
